/**
 * ADR-169 D8 — the Add Position and Grade dialog's date rule, mirroring the
 * server's overlap guards (O1/O2). The server stays the authority; this only
 * keeps the operator away from dates it would refuse.
 *
 * A new entry runs from its start with no end, so it must start AFTER every
 * existing entry: after a closed entry's end, and after the open entry's
 * start (the open one is closed the day before). Boundaries are inclusive:
 * an entry ending 01/05/2025 allows a start of 02/05/2025.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const day = (value) => dayjs.utc(value).format('YYYY-MM-DD');

/** A leg closed before it started says nothing (server: isDegenerate). */
const isDegenerate = (entry) => entry.endDate && day(entry.endDate) < day(entry.startDate);

/**
 * The last day a new entry may NOT start on ('YYYY-MM-DD'), or null when
 * there is no history.
 * @param {Array<{startDate, endDate}>} history - position history entries
 */
export const lastBlockedDay = (history) => {
  const days = (history || [])
    .filter((entry) => entry && entry.startDate && !isDegenerate(entry))
    .map((entry) => day(entry.endDate || entry.startDate));
  if (days.length === 0) {
    return null;
  }
  return days.sort().pop();
};

/**
 * DatePicker shouldDisableDate predicate.
 * @param {Array} history
 * @returns {(candidate: dayjs.Dayjs) => boolean}
 */
export const disableOverlappingStart = (history) => {
  const blocked = lastBlockedDay(history);
  return (candidate) => {
    if (!blocked || !candidate) {
      return false;
    }
    return candidate.format('YYYY-MM-DD') <= blocked;
  };
};

/** Helper text for the Start Date field. */
export const startDateHint = (history) => {
  const blocked = lastBlockedDay(history);
  if (!blocked) {
    return 'Required — the day the change takes effect';
  }
  return `Required — must be after ${dayjs.utc(blocked).format('DD/MM/YYYY')} (no overlap with existing entries)`;
};
